import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  Save, 
  Eye, 
  EyeOff, 
  AlertCircle, 
  CheckCircle, 
  Key,
  Globe,
  RefreshCw,
  Info
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const SettingsPage = () => {
  // State management
  const [apiToken, setApiToken] = useState('');
  const [apiEndpoint, setApiEndpoint] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  
  const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const DEFAULT_ENDPOINT = 'https://fortressincomefund.azurewebsites.net/api/v1/marketingbuildings';

  // Load saved settings on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  // Load settings from localStorage
  const loadSettings = () => {
    try {
      const savedToken = localStorage.getItem('properties_api_token') || '';
      const savedEndpoint = localStorage.getItem('properties_api_endpoint') || DEFAULT_ENDPOINT;
      
      setApiToken(savedToken);
      setApiEndpoint(savedEndpoint);
      
      // Test connection if token exists
      if (savedToken) {
        testConnection(savedToken, savedEndpoint);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    }
  };

  // Save settings to localStorage
  const saveSettings = async () => {
    try {
      setLoading(true);
      
      // Validate inputs
      if (!apiEndpoint.trim()) {
        toast.error('API endpoint is required');
        return;
      }

      // Test connection before saving (but don't block saving if it fails)
      const isValid = await testConnection(apiToken, apiEndpoint);
      
      // Save to localStorage regardless of connection test result
      localStorage.setItem('properties_api_token', apiToken.trim());
      localStorage.setItem('properties_api_endpoint', apiEndpoint.trim());
      
      if (!isValid && apiToken.trim()) {
        toast.success('Settings saved successfully! Note: Connection test failed - please verify your token and endpoint.');
      } else {
        toast.success('Settings saved successfully!');
      }
      
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  // Test API connection
  const testConnection = async (token = apiToken, endpoint = apiEndpoint) => {
    if (!endpoint.trim()) {
      setConnectionStatus({ status: 'error', message: 'No endpoint configured' });
      return false;
    }

    try {
      setTestingConnection(true);
      setConnectionStatus({ status: 'testing', message: 'Testing connection...' });

      // Test the connection through our backend
      const response = await axios.get(`${API}/api/buildings/health`);
      
      if (response.data.api_token_configured && token.trim()) {
        // Test with actual token
        const testResponse = await axios.get(`${API}/api/buildings/marketing-buildings`);
        if (testResponse.status === 200) {
          setConnectionStatus({ 
            status: 'success', 
            message: `Connected successfully. Found ${testResponse.data.length} properties.` 
          });
          return true;
        }
      } else if (!token.trim()) {
        setConnectionStatus({ 
          status: 'warning', 
          message: 'No token configured. Will use demo data.' 
        });
        return true;
      }
      
      setConnectionStatus({ status: 'error', message: 'Connection failed' });
      return false;
      
    } catch (error) {
      console.error('Connection test error:', error);
      setConnectionStatus({ 
        status: 'error', 
        message: error.response?.data?.detail || 'Connection test failed' 
      });
      return false;
    } finally {
      setTestingConnection(false);
    }
  };

  // Reset to defaults
  const resetToDefaults = () => {
    setApiEndpoint(DEFAULT_ENDPOINT);
    setApiToken('');
    setConnectionStatus(null);
  };

  // Clear all settings
  const clearSettings = () => {
    localStorage.removeItem('properties_api_token');
    localStorage.removeItem('properties_api_endpoint');
    resetToDefaults();
    toast.success('Settings cleared');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Settings className="mr-3 h-6 w-6 text-cyan-600" />
            Application Settings
          </h2>
          <p className="text-gray-600 mt-1">
            Configure API tokens and endpoints for external integrations
          </p>
        </div>
      </div>

      {/* Properties API Configuration */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <Key className="h-5 w-5 text-cyan-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Properties API Configuration</h3>
        </div>
        
        <div className="space-y-4">
          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">How to get your API token:</p>
                <ol className="list-decimal list-inside space-y-1 text-blue-700">
                  <li>Contact your Fortress Income Fund administrator</li>
                  <li>Request API access credentials for the Lease Summary endpoint</li>
                  <li>Copy the Bearer token provided to you</li>
                  <li>Paste it in the field below and click "Save Token"</li>
                </ol>
                <p className="mt-2 text-blue-600">
                  <strong>Note:</strong> If no token is configured, the app will automatically use demo data.
                </p>
              </div>
            </div>
          </div>

          {/* API Endpoint */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Globe className="inline h-4 w-4 mr-1" />
              API Endpoint URL
            </label>
            <input
              type="url"
              value={apiEndpoint}
              onChange={(e) => setApiEndpoint(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              placeholder="https://api.example.com/endpoint"
            />
            <p className="text-xs text-gray-500 mt-1">
              Default: {DEFAULT_ENDPOINT}
            </p>
          </div>

          {/* API Token */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Key className="inline h-4 w-4 mr-1" />
              Properties API Token (Bearer Token)
            </label>
            <div className="relative">
              <input
                type={showToken ? 'text' : 'password'}
                value={apiToken}
                onChange={(e) => setApiToken(e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                placeholder="Enter your Bearer token here..."
              />
              <button
                type="button"
                onClick={() => setShowToken(!showToken)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showToken ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              This token will be stored locally in your browser and used for API authentication.
            </p>
          </div>

          {/* Connection Status */}
          {connectionStatus && (
            <div className={`border rounded-lg p-4 ${
              connectionStatus.status === 'success' 
                ? 'bg-green-50 border-green-200' 
                : connectionStatus.status === 'warning'
                ? 'bg-yellow-50 border-yellow-200'
                : connectionStatus.status === 'testing'
                ? 'bg-blue-50 border-blue-200'
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center">
                {connectionStatus.status === 'success' && <CheckCircle className="h-5 w-5 text-green-600 mr-2" />}
                {connectionStatus.status === 'warning' && <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />}
                {connectionStatus.status === 'testing' && <RefreshCw className="h-5 w-5 text-blue-600 mr-2 animate-spin" />}
                {connectionStatus.status === 'error' && <AlertCircle className="h-5 w-5 text-red-600 mr-2" />}
                <span className={`text-sm font-medium ${
                  connectionStatus.status === 'success' 
                    ? 'text-green-800' 
                    : connectionStatus.status === 'warning'
                    ? 'text-yellow-800'
                    : connectionStatus.status === 'testing'
                    ? 'text-blue-800'
                    : 'text-red-800'
                }`}>
                  {connectionStatus.message}
                </span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={saveSettings}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50"
            >
              <Save size={16} className="mr-2" />
              {loading ? 'Saving...' : 'Save Settings'}
            </button>
            
            <button
              onClick={() => testConnection()}
              disabled={testingConnection}
              className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50"
            >
              <RefreshCw size={16} className={`mr-2 ${testingConnection ? 'animate-spin' : ''}`} />
              Test Connection
            </button>
            
            <button
              onClick={resetToDefaults}
              className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
            >
              Reset to Defaults
            </button>
            
            <button
              onClick={clearSettings}
              className="flex items-center px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Clear All
            </button>
          </div>
        </div>
      </div>

      {/* Security Notice */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
          <div className="text-sm text-yellow-800">
            <p className="font-medium mb-1">Security Notice</p>
            <p>
              API tokens are stored locally in your browser's localStorage. They are not transmitted to our servers 
              except when making authenticated requests to the configured API endpoint. 
              Clear your browser data to remove stored tokens.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;